import {
  FIRST_COLUMN_END,
  LINE_LENGTH,
  POSITION_COUNT_LENGTH,
  POSITION_PRICE_LENGTH,
  THIRD_COLUMN_END,
} from "@/services/backend/connect/components/string-builder-helpers";
import { useMemo } from "react";
import { Position } from "@/services/backend/htz/work-order/types/work-order";

const euroFormatter = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  currencyDisplay: "code",
});

const floatFormatter = new Intl.NumberFormat("de-DE", {
  style: "decimal",
  maximumFractionDigits: 2,
});

export function useHtzPositionPrices(positions?: Position[]): string {
  return useMemo(() => {
    if (!positions) return "";
    return htzPositionPrices(positions);
  }, [positions]);
}

function htzPositionPrices(positions: Position[]): string {
  // Limit to completed positions
  const completed = positions.filter((p) => p.completed);

  // Positions are grouped in the offer. This is re-built here.
  // Note that the original position on the offer is lost.
  const grouped = Object.groupBy(
    completed,
    ({ title, unitPrice }) => `${title}-${unitPrice}`,
  );

  // We fold the grouped positions into one
  const summed = Object.values(grouped).map((group, idx) => ({
    number: idx + 1,
    title: group?.reduce((_, c) => c.title, "") ?? "",
    unitCount: group?.reduce((p, c) => c.unitCount + p, 0) ?? 0,
    unitPrice: group?.reduce((_, c) => c.unitPrice, 0) ?? 0,
    totalPrice: group?.reduce((p, c) => c.totalPrice + p, 0) ?? 0,
  }));

  // Build position lines
  const lines: string[] = summed.map((position) => {
    let line: string = "";
    // First column: Position Number
    line = position.number.toString();
    line = line.padEnd(FIRST_COLUMN_END);
    // Second column: unit count
    let count = floatFormatter.format(position.unitCount);
    count += "    ";
    count = count.padStart(POSITION_COUNT_LENGTH);
    line += count;
    // Third column: title & price per unit
    line += `${position.title} á ${euroFormatter.format(position.unitPrice)}`;
    line = line.padEnd(THIRD_COLUMN_END);
    // Fourth Column: Price
    let price = euroFormatter.format(position.totalPrice);
    price = price.padStart(POSITION_PRICE_LENGTH);
    line += price;

    return line;
  });

  // Add total line
  lines.push(separatorLine);
  let totalLine = "Gesamtpreis";
  totalLine = totalLine.padEnd(THIRD_COLUMN_END);
  const totalPrice = completed.reduce(
    (prev, curr) => curr.totalPrice + prev,
    0,
  );
  totalLine += euroFormatter.format(totalPrice).padStart(POSITION_PRICE_LENGTH);
  lines.push(totalLine);

  return lines.join("\n");
}

const separatorLine = "-".repeat(LINE_LENGTH);
